import React, { useMemo } from 'react';

import { Drawer, IconButton, makeStyles } from '@material-ui/core';
import { ChevronLeftOutlined } from '@material-ui/icons';

import { clsx, colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

import {
  NOT_FOUND_PURCHASE_ORDER,
  PREVIOUS_PURCHASE_ORDER_ITEM
} from '../assets';

import { useSelector } from '@store';

import { documentDateFormat, formatCurrency } from '@utils/formatter';
import useDocuments from '@hooks/useDocuments';

interface ItemProps {
  id: string;
  createdAt: string;
  quotationPrice: number;
  dueDate: string;
  isLatest?: boolean;
  onClick?: (id: string) => void;
}

interface Props {
  title: string;
  items: ItemProps[];
  onClick: (id: string) => void;
}

const QuotationListDrawer = ({ items, title, onClick }: Props) => {
  const classes = useStyles();

  const { onUpdateDocumentsOptions } = useDocuments();

  const anchorId = useSelector(
    (state) => state.documents.options?.listAnchorId
  );

  const anchorEl = useMemo(
    () => (anchorId ? document.getElementById(anchorId) : null),
    [anchorId]
  );

  const open = Boolean(anchorEl);

  const onClose = () => {
    onUpdateDocumentsOptions({ listAnchorId: null });
  };

  return (
    <Drawer
      open={open}
      variant="persistent"
      anchor={'right'}
      transitionDuration={200}
      onClose={onClose}
      classes={{
        paper: classes.paper
      }}
    >
      <div className={classes.header}>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="500"
        >
          {title}
        </CustomizedTypography>
        <IconButton className="close" onClick={onClose}>
          <ChevronLeftOutlined />
        </IconButton>
      </div>

      <div
        className={clsx(classes.content, {
          notfound: items.length === 0
        })}
      >
        {items.length === 0 && <NotFound label="발주서" />}

        {items.map(({ createdAt, quotationPrice, dueDate, id }, index) => (
          <div
            className={classes.item}
            key={index}
            onClick={() => onClick && onClick(id)}
          >
            <img
              width={56}
              height={56}
              src={PREVIOUS_PURCHASE_ORDER_ITEM}
              alt=""
            />

            <div className={classes.itemContent}>
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="500"
              >
                {documentDateFormat(createdAt)}
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                color="textSecondary"
                fontWeight="500"
              >
                총견적&nbsp;・&nbsp;
                {formatCurrency(quotationPrice)}원
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                color="textSecondary"
                fontWeight="500"
              >
                납기일&nbsp;・&nbsp;발주일로부터 {dueDate}일
              </CustomizedTypography>
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
};

export default QuotationListDrawer;

const NotFound = ({ label }: any) => {
  return (
    <>
      <img src={NOT_FOUND_PURCHASE_ORDER} alt="" className="notfound-img" />
      <CustomizedTypography variant="body1" color="textSecondary">
        아직 {label}를 발행하지 않았습니다.
      </CustomizedTypography>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    possiton: 'relative',
    width: '100%',
    borderBottom: `1px solid ${colorSet.gray400}`,
    padding: 12,
    '& .close': {
      position: 'absolute',
      left: 12,
      top: 12,
      padding: 0,
      '& svg': {
        width: 24,
        height: 24
      }
    }
  },
  paper: {
    zIndex: 5000,
    width: '100%'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    '&.notfound': {
      justifyContent: 'center',
      alignItems: 'center'
    },
    '& .notfound-img': {
      width: 137,
      height: 137,
      marginBottom: 32
    }
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '20px 16px',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: colorSet.gray100
    }
  },
  itemContent: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 12
  },
  chip: {
    padding: '4px 8px',
    borderRadius: 100,
    position: 'absolute',
    top: 20,
    right: 16,
    '&.latest': {
      backgroundColor: colorSet.primary200,
      color: colorSet.primary500,
      ...theme.typography.caption
    },
    '&.reject': {
      backgroundColor: colorSet.errorBackGround,
      color: colorSet.errorMain,
      ...theme.typography.caption
    }
  }
}));
